import { Location } from '@angular/common';
import { Component, HostListener, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Navigate } from '@ngxs/router-plugin';
import { Store } from '@ngxs/store';
import { AudienceWidget } from 'app/library/audience/models/audience-widget.model';
import { MaxBrainAudience } from 'app/library/audience/models/audience.model';
import { AudienceExperienceStatusEnum } from 'app/library/audience/enums/audience-experience-status.enum';
import { AudienceApiService } from 'app/library/audience/services/audience.api-service';
import { AudienceService } from 'app/library/audience/services/audience.service';
import { AudienceRelevantExperience } from 'app/library/grow/models/audience-relevant-experience.model';
import { SidenavService } from 'app/library/grow/services/sidenav.service';
import { ScheduledStreamContentService } from 'app/library/scheduled-stream-content/services/scheduled-stream-content.service';
import { EngageEditFeaturedContentDialogComponent } from 'app/library/widgets/dialogs/engage-edit-featured-content-dialog/engage-edit-featured-content-dialog.component';
import { MaxBrainWidget } from 'app/library/widgets/models/widget.model';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';

@Component({
    selector: 'app-audience-widgets',
    templateUrl: './audience-widgets.component.html',
    styleUrls: ['./audience-widgets.component.scss'],
})
export class AudienceWidgetsComponent extends UnsubscribeOnDestroy implements OnInit {
    engageEditAudienceWidgetDialogRef: MatDialogRef<EngageEditFeaturedContentDialogComponent>;
    audience: MaxBrainAudience;
    isEditAudienceWidgetDialogOpening = false;

    stream: { headerBannerStream: AudienceWidget; headerStream: AudienceWidget[] } = {
        headerBannerStream: null,
        headerStream: [],
    };

    widgets: MaxBrainWidget[] = [];
    audienceId: string;
    audienceRelevantExperiences: AudienceRelevantExperience[] = [];

    screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

    @HostListener('window:resize')
    onResize() {
        this.screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    }

    constructor(
        public sidenavService: SidenavService,
        private _route: ActivatedRoute,
        private _audienceService: AudienceService,
        private _featuredStreamContentService: ScheduledStreamContentService,
        private _location: Location,
        private _store: Store,
        private _matDialog: MatDialog,
        private _audienceApiService: AudienceApiService
    ) {
        super();
    }

    private async loadAudience(audienceId: string): Promise<MaxBrainAudience | null> {
        let audience = this._audienceService.getAudienceById(audienceId);
        if (audience) {
            return audience;
        }

        // Fallback to fetching the single audience by id.
        // `fetchAudiences()` loads only a list (often root audiences) and may not contain this id.
        try {
            audience = await this._audienceApiService.read(audienceId).toPromise();
            return audience ?? null;
        } catch {
            return null;
        }
    }

    async ngOnInit() {
        this.audienceId = this._route.parent.snapshot.params['id'];

        const audienceWidgets = await this._audienceService.fetchAudienceWidgets(this.audienceId);
        this.audienceRelevantExperiences = await this._audienceService.fetchAudienceRelevantExperiences(
            this.audienceId,
            AudienceExperienceStatusEnum.RELEVANT
        );

        for (let i = 0; i < audienceWidgets.length; i++) {
            const widget = audienceWidgets[i];
            if (widget.name === 'header_banner_stream') {
                this.stream.headerBannerStream = widget;
            } else if (widget.name.startsWith('header_stream')) {
                this.stream.headerStream.push(widget);
            } else {
                this.widgets.push(widget);
            }
        }

        this.audience = await this.loadAudience(this.audienceId);
        // set default values
        if (this.stream.headerStream.length) {
            this.stream.headerStream.map((headerStream) => (headerStream.isWidgetEmpty = false));
        }
        if (this.stream.headerBannerStream) {
            this.stream.headerBannerStream.isWidgetEmpty = false;
        }

        this.widgets.forEach((widget) => {
            widget.isWidgetEmpty = false;
        });

        if (!this.audience?.emptyWidgets) {
            return;
        }

        this.audience.emptyWidgets.forEach((emptyWidgetId) => {
            if (this.stream.headerBannerStream?.id === String(emptyWidgetId)) {
                this.stream.headerBannerStream.isWidgetEmpty = true;
            }

            this.stream.headerStream.map((headerStream) => {
                if (headerStream.id === String(emptyWidgetId)) {
                    headerStream.isWidgetEmpty = true;
                }
            });

            this.widgets = this.widgets.map((widget) => {
                if (widget.id === String(emptyWidgetId)) {
                    return { ...widget, isWidgetEmpty: true };
                }
                return widget;
            });
        });
    }

    navigateToChangeExperience(): void {
        const audienceId = this.audience?.id ?? this.audienceId;
        if (!audienceId) {
            return;
        }
        this._store.dispatch(new Navigate([`/admin/grow/audiences/${audienceId}/relevant-experiences`]));
    }

    async openEditAudienceWidgetDialog(widgetName: string, featuredStreamContentId: string) {
        if (this.isEditAudienceWidgetDialogOpening) {
            return false;
        }

        this.isEditAudienceWidgetDialogOpening = true;

        this.engageEditAudienceWidgetDialogRef = this._matDialog.open(EngageEditFeaturedContentDialogComponent, {
            disableClose: false,
            width: '991px',
            autoFocus: false,
        });

        this.engageEditAudienceWidgetDialogRef.componentInstance.featuredStreamContentId = featuredStreamContentId;
        this.engageEditAudienceWidgetDialogRef.componentInstance.widgetName = widgetName;
        this.engageEditAudienceWidgetDialogRef.componentInstance.audienceId = this.audienceId;
        this.engageEditAudienceWidgetDialogRef.componentInstance.audienceRelevantExperiences = this.audienceRelevantExperiences;

        this.isEditAudienceWidgetDialogOpening = false;

        this.engageEditAudienceWidgetDialogRef.afterClosed().subscribe(async () => {
            // Fetch audiences to refresh list of audiences in a sidebar and show indicator on right place MAX-6698
            await this._audienceService.fetchAudiences();
            this._audienceService.getAudiences();

            this.engageEditAudienceWidgetDialogRef = null;
        });
    }

    goBack() {
        this._location.back();
    }
}
